import {ImageMetadata} from "../models/remoteConstants.model";
import {apiBindings} from "./constants";
import {effectiveScale} from "./imageScale";

// PixelMultiplier (Pixels Per Unit) is treated as a COMPUTED OUTPUT, not an
// authored field. The author tunes `scale` in the Workshop; at export we bake
// it into pixelMultiplier so the client renders the sprite at the editor's
// WYSIWYG size with no client-side changes.
//
// The client sizes a sprite as:  world_width = textureWidth / pixelMultiplier.
// We want scale=1 to mean "the full image frame spans one tile" (one world
// unit) — matching the Workshop, where ITEM_DEFAULT_W (= one tile) × scale is
// the rendered width. So:
//
//     world_width = scale × SCALE1_WORLD_WIDTH
//     textureWidth / pixelMultiplier = scale × SCALE1_WORLD_WIDTH
//     pixelMultiplier = textureWidth / (scale × SCALE1_WORLD_WIDTH)
//
// One tile ≈ one world unit (grid cellSize.x = 1), so SCALE1_WORLD_WIDTH = 1.
// (Existing tile art is 256px at PPU 250 ≈ 1.02 tiles; this lands tiles at an
// exact 1.0 tile. Nudge this constant if you want to match the old 250.)
export const SCALE1_WORLD_WIDTH = 1;

// The single PPU formula, shared by the export bake and the upload-time seed so
// they can never drift. pixelMultiplier = srcWidth / (scale × SCALE1_WORLD_WIDTH).
// Left as a float (Unity accepts a float pixelsPerUnit) so small source images
// stay exact — rounding a 16px sprite would otherwise drift its size a few %.
export function computePixelMultiplier(srcWidth: number, scale: number | undefined): number {
    return srcWidth / (effectiveScale(scale) * SCALE1_WORLD_WIDTH);
}

// Fallback only: read an image's natural width over the network, for images
// uploaded before srcWidth was captured. No CORS needed (dimensions, not
// pixels). Returns undefined if the image can't be read. Also used by the
// live-preview bake (which caches results per session).
export function loadNaturalWidth(url: string): Promise<number | undefined> {
    return new Promise(resolve => {
        const img = new Image();
        img.onload = () => resolve(img.naturalWidth || undefined);
        img.onerror = () => resolve(undefined);
        img.src = url;
    });
}

// Return a new images map with pixelMultiplier recomputed from each image's
// stored srcWidth + scale, so sprite world size is resolution-independent. Pure:
// the input map (and the editor's live state) is not mutated. Images uploaded
// before srcWidth was captured fall back to a network read of the file. THROWS
// if any image with a file has no resolvable width — better to block the export
// loudly than ship a wrong size (the old silent fallback to the 800 default).
export async function bakeImagePixelMultipliers(
    images: {[key: string]: ImageMetadata},
): Promise<{[key: string]: ImageMetadata}> {
    const keys = Object.keys(images);
    const unresolved: string[] = [];

    const baked = await Promise.all(keys.map(async key => {
        const entry = images[key];
        // Legacy string-valued entries carry no metadata to bake.
        if (typeof entry !== 'object' || entry === null) return entry;
        // Metadata-only placeholder with no image file yet — nothing to size.
        if (!entry.fileName) return entry;

        let width = entry.srcWidth;
        if (!width) width = await loadNaturalWidth(`${apiBindings.images}${entry.fileName}`);
        if (!width) { unresolved.push(key); return entry; }

        return {...entry, pixelMultiplier: computePixelMultiplier(width, entry.scale)};
    }));

    if (unresolved.length > 0) {
        throw new Error(
            `Export blocked: couldn't determine the pixel size of ${unresolved.length} image(s), ` +
            `so their in-game size would be wrong. Re-upload them (or check the files): ` +
            unresolved.join(', ')
        );
    }

    const out: {[key: string]: ImageMetadata} = {};
    keys.forEach((key, i) => { out[key] = baked[i]; });
    return out;
}
